import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { AppRoute, CustomRoutes, SettingRoutes } from '../../routes';
import { Badge } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../../context/cartContext';
import blackButterflyLogo from "../../assets/images/blackButterflyLogo.png";
import { styled } from '@mui/material/styles';
import './Navbar.css';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: "#a86d1e", 
    color: 'white',             
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`, 
    fontSize: '14px',
    minWidth: '22px',
    height: '22px',
  },
}));

function NavBar() {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { totalItems, totalCost, items } = useCart();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const { route, signOut } = useAuthenticator((context) => [
    context.route,
    context.signOut,
  ]);

  const navigate = useNavigate();

  function logOut() {
    signOut();
    navigate('/login');
  }

  return (
    <AppBar className='navbar-container'>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'white',
              textDecoration: 'none',
            }}
          >
            <img className='companyLogo' src={blackButterflyLogo} alt="" />
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClick={handleCloseNavMenu}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {CustomRoutes.map((cRo: AppRoute) => (
                cRo.NavBarVisibile ? 
                  <MenuItem key={cRo.DisplayName} onClick={() => navigate(cRo.Navigation)}>
                    <Typography textAlign="center">{cRo.DisplayName}</Typography>
                  </MenuItem>
                  :
                  null
              ))}
            </Menu>
          </Box>

          <Typography
            variant="h5"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
           <img className='companyLogo' src={blackButterflyLogo} alt="" />
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {CustomRoutes.map((cRo: AppRoute) => (
              cRo.NavBarVisibile ? 
                <Button
                className='links'
                  key={cRo.DisplayName}
                  onClick={() => navigate(cRo.Navigation)}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  {cRo.DisplayName}
                </Button>
                :
                null
            ))}
          </Box>

          <Box>
            <IconButton color="inherit" onClick={() => { navigate('/cart'); handleClose(); }}>
              <StyledBadge badgeContent={totalItems} color="secondary">
                <ShoppingCartIcon className='shoppingCartIcon'/>
              </StyledBadge>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                style: {
                  width: '300px',
                },
              }}
            >
              {items.length === 0 ? (
                <MenuItem>Your cart is empty.</MenuItem>
              ) : (
                items.map((item: { id: number; name: string; quantity: number; price: number }) => (
                  <MenuItem key={item.id} onClick={handleClose}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <Typography>{item.name}</Typography>
                      <Typography>{item.quantity} x ${item.price.toFixed(2)}</Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
              <MenuItem>
                <Typography textAlign="center" width="100%">View Cart</Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            {route !== 'authenticated' ? (
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                <Button
                  className='links'
                  key={"Login"}
                  onClick={() => navigate('/login')}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  Login
                </Button>
              </Box>
            ) 
            : (
              <>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar alt="Test User" src="/static/images/avatar/2.jpg" />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {SettingRoutes.map((setting: AppRoute) => (
                    <MenuItem key={setting.DisplayName} onClick={() => navigate(setting.Navigation)}>
                      <Typography textAlign="center">{setting.DisplayName}</Typography>
                    </MenuItem>
                  ))}
                  <MenuItem key={"logout"} onClick={() => logOut()}>
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default NavBar;
